import { createReducer, on } from '@ngrx/store';
import { MULTI_CONTRACT_MAX_SELECTION_ALLOWED } from '../../../constants/default.const';
import { ContractCP, ContractDetail } from '../../../models/contract-model';
import {
    clearContractDetailInStore,
    clearContractinStore,
    closePayment,
    continueMultipayment,
    failLoadContractDetails,
    increasePageNumberContract,
    loadContractAction,
    openContract,
    resetContractsLatestToShow,
    resetPageNumberContract,
    setContractDetailData,
    SetContractDetailLineItemsData,
    setContractDetailPaymentData,
    setNoMoreRecord,
    setPaymentOpen,
    setQuoteConversionInProgress,
    markQuoteConverted,
    setSelectedContracts,
    setTrackedItems,
    unsetSelectedContracts
} from './contract.actions';
import { continueInvoiceMultipayment } from '../invoices/invoices.actions';
import { TrackingResponse } from '../../../models/tracking-result.model';

export const contractsFeatureKey = 'contracts';

export interface ContractState {
    pageNumber: number;
    contracts: ContractCP[];
    selectedContracts: string[];
    noMoreRecord: boolean;
    selectedContractId: string;
    paymentOpen: boolean;
    error: string | null;
    contractDetail: ContractDetail | null;
    trackedItems?: TrackingResponse[];
    quoteConversionInProgress: boolean;
    convertedContractIds: string[];
}

export const initialState: ContractState = {
    pageNumber: 1,
    contracts: [],
    selectedContracts: [],
    noMoreRecord: false,
    selectedContractId: '',
    paymentOpen: false,
    error: null,
    contractDetail: null,
    quoteConversionInProgress: false,
    convertedContractIds: []
};

export const contractReducer = createReducer(
    initialState,
    on(loadContractAction, (state: ContractState, { contracts, pageSize = 25, showInList = true }: { contracts: ContractCP[]; pageSize?: number; showInList?: boolean }) => {
        if (contracts && contracts.length > 0) {
            // Honor showInList; preserve a row already visible in the list so opening a verified
            // contract's detail (shared fetchContractDetail$ path) never hides it. ADO-123726.
            const latestContracts: ContractCP[] = contracts.slice(0, pageSize).map(contract => ({
                ...contract,
                latestToShow: showInList || (state.contracts.find(c => c.contractId === contract.contractId)?.latestToShow ?? false)
            }));
            const filteredContractsWithLatestFlagReset = state.contracts.filter(contract => !latestContracts.some(latestContract => latestContract.contractId === contract.contractId));
            // Append the remaining contracts without the `latestToShow` flag
            const remainingContracts: ContractCP[] = filteredContractsWithLatestFlagReset.slice(latestContracts.length).map(contract => ({
                ...contract,
                latestToShow: false
            }));

            /**
             * Remove duplicates contracts and keep only latest contract as top
             */
            const uniqueContracts = [...latestContracts, ...filteredContractsWithLatestFlagReset, ...remainingContracts].reduce<ContractCP[]>((acc, contract) => {
                if (!acc.find(c => c.contractId === contract.contractId)) {
                    acc.push(contract);
                }
                return acc;
            }, []);
            return {
                ...state,
                contracts: uniqueContracts
            };
        }
        return state;
    }),
    on(setNoMoreRecord, (state: ContractState, { record }: { record: boolean }) => {
        return { ...state, noMoreRecord: record };
    }),
    /**
     * Reset pagination alongside the list: `pageNumber` so a later Load More cannot skip a page
     * (ADO-109731), and `noMoreRecord` because nothing ever sets it back to false for contracts --
     * a latched flag hides the entire Load More control, and the store outlives logout.
     */
    on(clearContractinStore, (state: ContractState) => {
        return { ...state, contracts: [], pageNumber: 1, noMoreRecord: false };
    }),
    on(increasePageNumberContract, (state: ContractState) => {
        return { ...state, pageNumber: state?.pageNumber + 1 };
    }),
    on(resetPageNumberContract, (state: ContractState) => {
        return { ...state, pageNumber: 1 };
    }),
    on(openContract, (state: ContractState, { selectedContractId }: { selectedContractId: string }) => {
        return { ...state, selectedContractId };
    }),
    on(setSelectedContracts, (state: ContractState, { selectedContracts }: { selectedContracts: string[] }) => {
        if (state.selectedContracts.length <= MULTI_CONTRACT_MAX_SELECTION_ALLOWED) {
            return { ...state, selectedContracts };
        }

        // Return the state unchanged if the condition is not met
        return state;
    }),
    on(unsetSelectedContracts, (state: ContractState, { unselectContracts }) => ({
        ...state,
        selectedContracts: state.selectedContracts.filter(contractId => !unselectContracts.includes(contractId))
    })),
    on(resetContractsLatestToShow, (state: ContractState) => ({
        ...state,
        contracts: state.contracts.map(contract => ({
            ...contract,
            latestToShow: false
        }))
    })),
    on(setPaymentOpen, (state: ContractState, { paymentOpen }) => {
        return { ...state, paymentOpen };
    }),
    on(closePayment, (state: ContractState) => {
        return { ...state, paymentOpen: false };
    }),
    // ADO-116163: the invoice multipay continue must open the POR Pay modal too. paymentOpen and the
    // modal live in the contract domain, so the invoice continue action shares this handler (mirrors
    // the closePayment reset which is already subject-agnostic).
    on(continueMultipayment, continueInvoiceMultipayment, (state: ContractState) => {
        return { ...state, paymentOpen: true };
    }),
    on(failLoadContractDetails, (state: ContractState, { error }) => {
        return { ...state, contracts: [], error };
    }),
    on(setContractDetailData, (state: ContractState, { data }) => {
        return { ...state, contractDetail: data };
    }),
    on(SetContractDetailLineItemsData, (state, { data }) => {
        if (!state.contractDetail) return state;

        return {
            ...state,
            contractDetail: {
                ...state.contractDetail,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                LineItems: data
            }
        };
    }),

    on(setContractDetailPaymentData, (state: ContractState, { data }) => {
        if (!state.contractDetail) return state;
        return {
            ...state,
            contractDetail: {
                ...state.contractDetail,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                Payment: data
            }
        };
    }),
    on(clearContractDetailInStore, (state: ContractState) => {
        return { ...state, selectedContractId: '', contractDetail: null };
    }),
    on(setTrackedItems, (state: ContractState, { trackedItems }) => {
        return { ...state, trackedItems };
    }),
    on(setQuoteConversionInProgress, (state: ContractState, { inProgress }) => {
        return { ...state, quoteConversionInProgress: inProgress };
    }),
    on(markQuoteConverted, (state: ContractState, { contractId }) => {
        return state.convertedContractIds.includes(contractId) ? state : { ...state, convertedContractIds: [...state.convertedContractIds, contractId] };
    })
);
